// Activity-feed for a task: list of existing updates + composer that supports
// @-mention autocomplete. mentions are explicit user-ids tracked alongside the
// body — server doesn't re-parse the text, it just trusts what we send.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Bot, Trash2 } from "lucide-preact";
import type { TaskUpdateItem, UserPublic } from "@juuplan/shared";
import {
  deleteTaskUpdate,
  fetchTaskUpdates,
  postTaskUpdate,
} from "../store/project";
import { useUsers, findUser } from "../store/users";
import { useMe } from "../lib/auth";
import { toastError } from "../lib/toast";
import { UserAvatar } from "./UserAvatar";
import { BotBadge, BotBadgeFor, BotOwnerNote, botTitle } from "./BotBadge";

import { Button } from "./ui/Button";

interface Props {
  taskId: number;
}

interface MentionMatch {
  /** start index in body where "@" sits. */
  start: number;
  /** chars after the "@" up to cursor (used for filtering). */
  query: string;
}

function findOpenMention(body: string, cursor: number): MentionMatch | null {
  // Walk back from cursor until we hit a whitespace or beginning of string.
  // If the run starts with "@" the user is composing a mention.
  if (cursor === 0) return null;
  let i = cursor - 1;
  while (i >= 0) {
    const ch = body[i];
    if (!ch) break;
    if (ch === "@") {
      return { start: i, query: body.slice(i + 1, cursor) };
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "gerade eben";
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} Min.`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `vor ${hr} Std.`;
  const days = Math.round(hr / 24);
  if (days < 7) return `vor ${days} Tag${days === 1 ? "" : "en"}`;
  return new Date(iso).toLocaleDateString("de-DE");
}

function highlightBody(body: string, mentionIds: number[]): preact.JSX.Element {
  // Replace each "@Word..." run with a chip if the following name matches any
  // user in mentions[]. The greedy match takes up to 60 chars / next "@".
  const knownNames = new Map<string, number>();
  for (const id of mentionIds) {
    const u = findUser(id);
    if (u) knownNames.set(u.name, id);
  }
  if (knownNames.size === 0) {
    return <span class="whitespace-pre-wrap break-words">{body}</span>;
  }
  const parts: (string | preact.JSX.Element)[] = [];
  let cursor = 0;
  // Match `@Name` where Name is one of the known mention names. Sort by length
  // desc so longer names win over prefixes ("Bob Smith" vs "Bob").
  const sortedNames = [...knownNames.keys()].sort((a, b) => b.length - a.length);
  while (cursor < body.length) {
    const at = body.indexOf("@", cursor);
    if (at === -1) {
      parts.push(body.slice(cursor));
      break;
    }
    if (at > cursor) parts.push(body.slice(cursor, at));
    let matchedName: string | null = null;
    for (const n of sortedNames) {
      if (body.startsWith(`@${n}`, at)) {
        matchedName = n;
        break;
      }
    }
    if (matchedName) {
      // Bot-Erwähnungen heben sich farblich ab (tertiary statt primary) und
      // tragen das Bot-Glyph, damit im Fließtext niemand einen Menschen
      // vermutet.
      const mentionedId = knownNames.get(matchedName);
      const mentioned = findUser(mentionedId);
      const mentionIsBot = mentioned?.is_bot === true;
      parts.push(
        <span
          key={`m-${at}`}
          class={`inline-flex items-center gap-0.5 rounded px-1 font-medium ${
            mentionIsBot
              ? "bg-[color:var(--color-tertiary-container)] text-[color:var(--color-on-tertiary-container)]"
              : "bg-[color:var(--color-primary-container)]/60 text-[color:var(--color-on-primary-container)]"
          }`}
          title={mentionIsBot ? botTitle(mentioned?.bot_owner_id) : undefined}
        >
          {mentionIsBot && <Bot size={14} aria-hidden="true" />}@{matchedName}
        </span>
      );
      cursor = at + 1 + matchedName.length;
    } else {
      // Bare "@" without a known name — render as plain text and move on.
      parts.push("@");
      cursor = at + 1;
    }
  }
  return <span class="whitespace-pre-wrap break-words">{parts}</span>;
}

export function UpdatesFeed(props: Props): preact.JSX.Element {
  const { taskId } = props;
  const me = useMe();
  const users = useUsers();
  const [items, setItems] = useState<TaskUpdateItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Getrennt von `items`: ein fehlgeschlagenes GET (api() toastet nur
  // Schreibfehler) hinterließ die Liste leer — nicht von "noch keine
  // Updates" zu unterscheiden.
  const [loadFailed, setLoadFailed] = useState(false);
  const [body, setBody] = useState("");
  const [mentions, setMentions] = useState<number[]>([]);
  const [guestVisible, setGuestVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [openMention, setOpenMention] = useState<MentionMatch | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    void fetchTaskUpdates(taskId).then(
      (rows) => {
        if (cancelled) return;
        setItems(rows);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setLoading(false);
        setLoadFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const candidates: UserPublic[] = useMemo(() => {
    if (!openMention) return [];
    const q = openMention.query.toLowerCase();
    return users.items
      .filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [openMention, users.items]);

  const handleBody = (next: string): void => {
    setBody(next);
    const ta = textareaRef.current;
    const cursor = ta?.selectionStart ?? next.length;
    setOpenMention(findOpenMention(next, cursor));
  };

  const insertMention = (user: UserPublic): void => {
    if (!openMention) return;
    const before = body.slice(0, openMention.start);
    const after = body.slice(openMention.start + 1 + openMention.query.length);
    const trail = after.startsWith(" ") || after === "" ? "" : " ";
    const next = `${before}@${user.name}${trail}${after}`;
    setBody(next);
    setMentions((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
    setOpenMention(null);
    // Reset focus + cursor right after the inserted mention.
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      const pos = before.length + 1 + user.name.length + trail.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const submit = async (): Promise<void> => {
    const trimmed = body.trim();
    if (!trimmed) return;
    // Keep only mentions whose name actually still appears in the body — the
    // user may have deleted the chip text by hand.
    const stillMentioned = mentions.filter((id) => {
      const u = findUser(id);
      return u && body.includes(`@${u.name}`);
    });
    setSubmitting(true);
    try {
      const created = await postTaskUpdate(taskId, {
        body: trimmed,
        mentions: stillMentioned,
        ...(guestVisible ? { guest_visible: true } : {}),
      });
      setItems((prev) => [...prev, created]);
      setBody("");
      setMentions([]);
      setGuestVisible(false);
      setOpenMention(null);
    } catch {
      // api.ts already toasted. Leave the composer state intact so the user
      // can edit + retry.
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async (item: TaskUpdateItem): Promise<void> => {
    try {
      await deleteTaskUpdate(taskId, item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch {
      toastError("Konnte Update nicht löschen.");
    }
  };

  return (
    <section>
      <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
        Aktivität
      </h3>

      {loading && items.length === 0 ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Lädt…
        </p>
      ) : loadFailed && items.length === 0 ? (
        <p class="text-xs text-[color:var(--color-error)]">
          Aktivität konnte nicht geladen werden.
        </p>
      ) : items.length === 0 ? (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Updates. Schreib das erste.
        </p>
      ) : (
        <ul class="flex flex-col gap-2 mb-3">
          {items.map((item) => {
            const own = me.user?.id === item.user_id;
            return (
              <li
                key={item.id}
                class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2 text-xs"
              >
                <header class="flex items-center gap-2 mb-1">
                  <UserAvatar
                    userId={item.user_id}
                    size={20}
                    name={item.author.name}
                    avatarUrl={item.author.avatar_url}
                    isBot={item.author.is_bot}
                    botOwnerId={item.author.bot_owner_id}
                  />
                  <span class="font-medium text-[color:var(--color-on-surface)]">
                    {item.author.name}
                  </span>
                  {/* Aus dem Autor-Payload, nicht aus dem Store: ein später
                      deaktivierter Bot fehlt dort und seine alten Beiträge
                      verlören sonst die Kennzeichnung. */}
                  <BotBadgeFor
                    isBot={item.author.is_bot}
                    ownerId={item.author.bot_owner_id}
                  />
                  <span class="text-[color:var(--color-on-surface-variant)]">
                    {relTime(item.created_at)}
                  </span>
                  {item.guest_visible && (
                    <span
                      class="inline-flex items-center rounded-full bg-[color:var(--color-primary-container)] px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-primary-container)]"
                      title="Dieses Update ist für externe Gäste sichtbar."
                    >
                      extern sichtbar
                    </span>
                  )}
                  {own && (
                    <button
                      type="button"
                      onClick={() => void doDelete(item)}
                      class="ml-auto rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                      aria-label="Update löschen"
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </header>
                <BotOwnerNote
                  isBot={item.author.is_bot}
                  ownerId={item.author.bot_owner_id}
                  className="-mt-1 mb-1 pl-7"
                />
                <div class="text-[color:var(--color-on-surface)]">
                  {highlightBody(item.body, item.mentions)}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div class="relative">
        <textarea
          ref={textareaRef}
          value={body}
          onInput={(e) => handleBody((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && openMention) {
              e.preventDefault();
              setOpenMention(null);
              return;
            }
            if (
              !openMention &&
              (e.key === "Enter") &&
              (e.metaKey || e.ctrlKey)
            ) {
              e.preventDefault();
              void submit();
            }
          }}
          rows={3}
          placeholder="Update schreiben… (@-Taste erwähnt Personen, Cmd/Strg+Enter sendet)"
          class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-shadow resize-y"
          aria-label="Neues Update schreiben"
        />
        {openMention && candidates.length > 0 && (
          <div
            class="absolute z-30 mt-1 max-h-56 w-64 overflow-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 py-1 text-xs"
            role="listbox"
            aria-label="Personen erwähnen"
          >
            {candidates.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => insertMention(u)}
                class="flex items-center gap-2 w-full text-left px-3 py-1.5 hover:bg-[color:var(--color-surface-container-low)]"
              >
                <UserAvatar userId={u.id} size={20} />
                {/* `flex-auto` statt `flex-1`: mit Basis 0 schrumpft der Name in
                    der schmalen Liste auf Breite 0, sobald die Zeile ueberlaeuft
                    — der Name verschwand dadurch hinter langen Adressen. */}
                <span class="flex-auto min-w-0 truncate text-[color:var(--color-on-surface)]">
                  {u.name}
                </span>
                {u.is_bot && <BotBadge ownerId={u.bot_owner_id} />}
                {/* Bot-Adressen liegen auf .invalid und sind reines Rauschen —
                    der Owner steht im Tooltip des Badges. */}
                {!u.is_bot && (
                  <span class="min-w-0 truncate text-[color:var(--color-on-surface-variant)] text-label">
                    {u.email}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div class="mt-2 flex items-center justify-between gap-3">
          <div class="flex flex-col gap-1 min-w-0">
            <span class="text-label text-[color:var(--color-on-surface-variant)] truncate">
              {mentions.length > 0
                ? `Erwähnt: ${mentions
                    .map((id) => findUser(id)?.name)
                    .filter(Boolean)
                    .join(", ")}`
                : "Tippe @ für Erwähnungen"}
            </span>
            <label class="inline-flex items-center gap-1.5 text-label text-[color:var(--color-on-surface-variant)] cursor-pointer">
              <input
                type="checkbox"
                checked={guestVisible}
                onChange={(e) =>
                  setGuestVisible((e.target as HTMLInputElement).checked)
                }
                class="accent-[color:var(--color-primary)]"
              />
              für Gast sichtbar
            </label>
          </div>
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={() => void submit()}
            disabled={!body.trim() || submitting}
          >
            {submitting ? "Sende…" : "Posten"}
          </Button>
        </div>
      </div>
    </section>
  );
}
